/**
 * Qué palabras cambiaron entre lo que el usuario dijo y la forma corregida.
 *
 * El problema: las correcciones se muestran como un par `dijiste → mejor así`
 * y el usuario tenía que comparar las dos frases palabra por palabra con el ojo
 * para descubrir dónde estaba el cambio. En una frase de 12 palabras donde sólo
 * una está mal ("some of the things that I noticed **is** that…" → "…**are**
 * that…") eso es trabajo perdido: la corrección está a la vista pero no se ve.
 *
 * Se deriva con el LCS compartido (`lib/tokenDiff.ts`) — el mismo que resuelve
 * la palabra foco de una card de Órbitas. NO hay un campo del backend con "la
 * palabra que cambió": el extractor de correcciones devuelve las dos frases
 * enteras, y lo que cambió ENTRE ellas es, por definición, la corrección.
 *
 * Best-effort por diseño, como en Órbitas: si el diff no es informativo se
 * devuelve vacío y el par se renderiza como siempre (sin marcas). Es
 * presentación, no configuración — no aplica el fail-fast de la casa, que existe
 * para valores que mal resueltos cambian qué se cobra o qué se sirve. Una marca
 * ausente no miente; una marca en la palabra equivocada enseña mal.
 */
import { useMemo } from 'react';
import { compareKey, diffIndices, dropNonWords, tokenizeText } from '../../lib/tokenDiff.js';

/** Índices (sobre los tokens de cada frase) de las palabras que cambiaron. */
export interface CorrectionMarks {
  /** Tokens de lo que dijo el usuario que sobran o están mal. */
  readonly said: ReadonlySet<number>;
  /** Tokens de la forma corregida que son nuevos (incluye lo que faltaba). */
  readonly fix: ReadonlySet<number>;
}

const EMPTY: CorrectionMarks = { said: new Set(), fix: new Set() };

/**
 * Techo de "esto ya no es una palabra, es otra oración". Si el diff marca más de
 * esta fracción de uno de los dos lados, la corrección no está cambiando UNA
 * palabra sino reescribiendo la frase — el caso típico de las `suggestion` de
 * naturalidad ("Give me a water, please." → "Could I have some water,
 * please?"). Ahí marcar es peor que no marcar: queda medio texto pintado, que no
 * señala nada, y manda el contraste dijiste/mejor que la fila ya muestra.
 *
 * Mismo valor que el de Órbitas (`target-words.ts`) y por la misma razón: las
 * dos frases son la MISMA oración con y sin el error, así que el diff es
 * confiable por construcción hasta que deja de haber una oración común.
 */
const REWRITE_FRACTION = 0.5;

/**
 * Las palabras que cambiaron entre `said` y `fix`. Sets vacíos = no hay nada
 * útil que marcar (frases idénticas, una vacía, un cambio de sola puntuación, o
 * una reescritura completa).
 */
export function correctionMarks(said: string, fix: string): CorrectionMarks {
  const a = tokenizeText(said).map(compareKey);
  const b = tokenizeText(fix).map(compareKey);
  if (a.length === 0 || b.length === 0) return EMPTY;
  const raw = diffIndices(a, b);
  const saidMarks = dropNonWords(raw.aOnly, a);
  const fixMarks = dropNonWords(raw.bOnly, b);
  // Ninguna PALABRA distinta: o la corrección es de pura puntuación (legítima,
  // pero sin palabra que señalar) o las dos frases son la misma.
  if (saidMarks.size === 0 && fixMarks.size === 0) return EMPTY;
  if (saidMarks.size > a.length * REWRITE_FRACTION) return EMPTY;
  if (fixMarks.size > b.length * REWRITE_FRACTION) return EMPTY;
  return { said: saidMarks, fix: fixMarks };
}

/** `correctionMarks` memoizado por par de frases (el LCS es O(n·m)). */
export function useCorrectionMarks(said: string, fix: string): CorrectionMarks {
  return useMemo(() => correctionMarks(said, fix), [said, fix]);
}
